<template>
  <div
    class="logistics-waybill-upload-panel proof-upload-panel"
    :class="{ 'is-paste-active': active }"
    tabindex="0"
    v-loading="panelLoading"
    :element-loading-text="panelLoadingText"
    @click="$emit('focus-panel')"
    @focus="$emit('focus-panel')"
  >
    <div class="proof-upload-list">
      <div v-for="(url, index) in photos" :key="'sw-' + url + '-' + index" class="proof-upload-item">
        <el-image :src="url" fit="cover" class="proof-upload-image" :preview-src-list="photos"></el-image>
        <i class="el-icon-error proof-upload-remove" @click.stop="removePhoto(index)"></i>
      </div>
      <el-upload
        class="proof-upload-add"
        action="/biz/oss/upload"
        name="file"
        accept="image/*"
        multiple
        :data="{ bizModule: 'logistic' }"
        :show-file-list="false"
        :before-upload="beforeUpload"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :disabled="panelLoading"
      >
        <div class="proof-upload-trigger">
          <i class="el-icon-plus"></i>
          <span class="proof-upload-text">添加图片</span>
        </div>
      </el-upload>
    </div>
    <div class="proof-upload-tip">{{ tip }}</div>
  </div>
</template>

<script>
import { ossModules } from "@r";
import { normalizeStringList } from "../../utils/logisticsFormHelpers";

export default {
  name: "LogisticsWaybillUploadPanel",
  props: {
    photos: {
      type: Array,
      default: () => [],
    },
    active: {
      type: Boolean,
      default: false,
    },
    uploadPending: {
      type: Number,
      default: 0,
    },
    barcodeRecognizing: {
      type: Boolean,
      default: false,
    },
    tip: {
      type: String,
      default: "支持多选上传；点击本区域后可 Ctrl+V 粘贴图片；上传后将自动识别条形码中的物流单号",
    },
    scanOnUpload: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    panelLoading() {
      return this.uploadPending > 0 || this.barcodeRecognizing;
    },
    panelLoadingText() {
      if (this.barcodeRecognizing) return "正在识别条形码…";
      if (this.uploadPending > 0) return "上传中…";
      return "加载中…";
    },
  },
  methods: {
    isProofImageFile(file) {
      if (!file) return false;
      if (file.type && file.type.indexOf("image/") === 0) return true;
      const name = (file.name || "").toLowerCase();
      return /\.(jpg|jpeg|png|gif|webp|bmp)$/.test(name);
    },
    beforeUpload(file) {
      if (!this.isProofImageFile(file)) {
        this.$message.error("请上传图片文件");
        return false;
      }
      this.$emit("upload-start");
      if (this.scanOnUpload) {
        this.scanBarcodes(file);
      }
      return true;
    },
    handleUploadSuccess(res) {
      this.$emit("upload-end");
      if (res && res.objData && res.objData.fileUrl) {
        this.$emit("update:photos", [...this.photos, res.objData.fileUrl]);
        return;
      }
      this.$message.error((res && res.message) || "上传失败");
    },
    handleUploadError() {
      this.$emit("upload-end");
      this.$message.error("上传失败");
    },
    removePhoto(index) {
      const next = this.photos.slice();
      next.splice(index, 1);
      this.$emit("update:photos", next);
    },
    async scanBarcodes(file) {
      this.$emit("barcode-start");
      try {
        const { scanLogisticsBarcodesFromFile } = await import("../../utils/logisticsBarcodeScanner");
        const result = await scanLogisticsBarcodesFromFile(file);
        this.$emit("barcode-result", {
          logisticsNos: normalizeStringList(result && result.logisticsNos),
          logisticsCompanies: normalizeStringList(result && result.logisticsCompanies),
        });
      } catch (e) {
        this.$emit("barcode-result", { logisticsNos: [], logisticsCompanies: [] });
      } finally {
        this.$emit("barcode-end");
      }
    },
    uploadPastedFile(file) {
      if (!this.isProofImageFile(file)) {
        this.$message.error("请粘贴图片文件");
        return;
      }
      if (this.scanOnUpload) {
        this.scanBarcodes(file);
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("bizModule", "logistic");
      this.$emit("upload-start");
      ossModules
        .upload(formData)
        .then(res => {
          if (res && res.objData && res.objData.fileUrl) {
            this.$emit("update:photos", [...this.photos, res.objData.fileUrl]);
            return;
          }
          this.$message.error((res && res.message) || "上传失败");
        })
        .catch(err => {
          this.$message.error((err && err.message) || "上传失败");
        })
        .finally(() => {
          this.$emit("upload-end");
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.logistics-waybill-upload-panel {
  outline: none;
  border: 1px dashed transparent;
  border-radius: 6px;
  padding: 4px;

  &.is-paste-active {
    border-color: #409eff;
    background: #f5f9ff;
  }

  &:focus {
    box-shadow: none;
  }

  .proof-upload-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .proof-upload-item {
    position: relative;
    width: 96px;
    height: 96px;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    overflow: hidden;
  }

  .proof-upload-image {
    width: 100%;
    height: 100%;
  }

  .proof-upload-remove {
    position: absolute;
    top: 2px;
    right: 2px;
    color: #f56c6c;
    font-size: 18px;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
  }

  .proof-upload-add ::v-deep .el-upload {
    width: 96px;
    height: 96px;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
  }

  .proof-upload-trigger {
    width: 96px;
    height: 96px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 12px;
  }

  .proof-upload-trigger .el-icon-plus {
    font-size: 20px;
    margin-bottom: 4px;
  }

  .proof-upload-tip {
    margin-top: 8px;
    color: #909399;
    font-size: 12px;
    line-height: 1.4;
  }
}
</style>
